import { useLoaderData } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface ShopifyOrderRow {
  id: string;
  shopifyOrderId: string;
  orderNumber: string | null;
  email: string | null;
  shopifyCreatedAt: string | null;
  shopifyUpdatedAt: string | null;
  closedAt: string | null;
  cancelledAt: string | null;
  financialStatus: string | null;
  fulfillmentStatus: string | null;
  totalPrice: number | null;
  subtotalPrice: number | null;
  totalTax: number | null;
  totalDiscounts: number | null;
  currency: string | null;
  sourceName: string | null;
  customerEmail: string | null;
  customerFirstName: string | null;
  customerLastName: string | null;
}

interface LineItem {
  id: string;
  title: string | null;
  sku: string | null;
  quantity: number | null;
  price: number | null;
  productId: string | null;
}

function formatCurrency(amount: number | null, currency?: string | null): string {
  if (amount === null || amount === undefined) return "\u2014";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency ?? "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "\u2014";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "\u2014";
  }
}

function statusColor(status: string | null): string {
  switch (status) {
    case "paid":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "refunded":
    case "voided":
      return "bg-red-100 text-red-800 border-red-200";
    case "partially_refunded":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "pending":
    case "authorized":
      return "bg-blue-100 text-blue-800 border-blue-200";
    default:
      return "";
  }
}

const FINANCIAL_STATUSES = [
  "paid",
  "pending",
  "refunded",
  "voided",
  "partially_refunded",
  "authorized",
  "partially_paid",
];

export function ShopifyOrdersPage() {
  const { orders: initialOrders, total: initialTotal } = useLoaderData({
    from: "/_authenticated/shopify-orders",
  }) as {
    orders: ShopifyOrderRow[];
    total: number;
  };

  const [orders, setOrders] = useState(initialOrders);
  const [total, setTotal] = useState(initialTotal);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("shopifyCreatedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ShopifyOrderRow | null>(null);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  const pageSize = 50;
  const totalPages = Math.ceil(total / pageSize);

  const fetchOrders = useCallback(
    async (
      p: number,
      s: string,
      status: string,
      sort: string,
      dir: string,
      signal?: AbortSignal,
    ) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(p),
          pageSize: String(pageSize),
          sortBy: sort,
          sortDir: dir,
        });
        if (s) params.set("search", s);
        if (status) params.set("financialStatus", status);

        const res = await fetch(`/api/shopify/orders?${params}`, { signal });
        if (!res.ok) throw new Error("Failed to fetch");
        const data = await res.json();
        setOrders(data.orders ?? []);
        setTotal(data.total ?? 0);
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        console.error("Failed to fetch orders:", e);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setPage(1);
      fetchOrders(1, search, statusFilter, sortBy, sortDir, controller.signal);
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [search, statusFilter, sortBy, sortDir, fetchOrders]);

  function handlePageChange(newPage: number) {
    setPage(newPage);
    fetchOrders(newPage, search, statusFilter, sortBy, sortDir);
  }

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("desc");
    }
  }

  async function handleSelectOrder(order: ShopifyOrderRow) {
    setSelectedOrder(order);
    try {
      const res = await fetch(`/api/shopify/orders/${order.id}`);
      if (res.ok) {
        const data = await res.json();
        setLineItems(data.lineItems ?? []);
      }
    } catch {
      setLineItems([]);
    }
  }

  function SortIcon({ column }: { column: string }) {
    if (sortBy !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-30" />;
    return sortDir === "asc" ? (
      <ArrowUp className="ml-1 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Shopify Orders"
        description={`${total.toLocaleString()} orders mirrored from Shopify`}
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by order # or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value=" ">All Statuses</SelectItem>
            {FINANCIAL_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {statusFilter && (
          <Button variant="ghost" size="sm" onClick={() => setStatusFilter("")}>
            <X className="mr-1 h-3 w-3" />
            Clear filter
          </Button>
        )}
      </div>

      <div className={`rounded-md border ${loading ? "opacity-60" : ""}`}>
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead>
                <button
                  type="button"
                  onClick={() => handleSort("orderNumber")}
                  className="flex items-center text-xs uppercase tracking-wider hover:text-foreground"
                >
                  Order <SortIcon column="orderNumber" />
                </button>
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={() => handleSort("totalPrice")}
                  className="flex items-center text-xs uppercase tracking-wider hover:text-foreground"
                >
                  Total <SortIcon column="totalPrice" />
                </button>
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Fulfillment</TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={() => handleSort("shopifyCreatedAt")}
                  className="flex items-center text-xs uppercase tracking-wider hover:text-foreground"
                >
                  Date <SortIcon column="shopifyCreatedAt" />
                </button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  {search || statusFilter
                    ? "No orders match your filters."
                    : "No orders synced yet."}
                </TableCell>
              </TableRow>
            ) : (
              orders.map((o) => (
                <TableRow
                  key={o.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSelectOrder(o)}
                >
                  <TableCell>
                    <span className="font-medium">{o.orderNumber ?? o.shopifyOrderId}</span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {o.customerFirstName || o.customerLastName
                      ? `${o.customerFirstName ?? ""} ${o.customerLastName ?? ""}`.trim()
                      : (o.customerEmail ?? o.email ?? "\u2014")}
                  </TableCell>
                  <TableCell className="tabular-nums font-medium">
                    {formatCurrency(o.totalPrice, o.currency)}
                  </TableCell>
                  <TableCell>
                    {o.financialStatus && (
                      <Badge
                        variant="outline"
                        className={`text-xs capitalize ${statusColor(o.financialStatus)}`}
                      >
                        {o.financialStatus.replace(/_/g, " ")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground capitalize">
                    {o.fulfillmentStatus ?? "unfulfilled"}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateTime(o.shopifyCreatedAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * pageSize + 1}\u2013{Math.min(page * pageSize, total)} of{" "}
            {total.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1 || loading}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages || loading}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <OrderDetailSheet
        order={selectedOrder}
        lineItems={lineItems}
        onClose={() => {
          setSelectedOrder(null);
          setLineItems([]);
        }}
      />
    </div>
  );
}

function OrderDetailSheet({
  order,
  lineItems,
  onClose,
}: {
  order: ShopifyOrderRow | null;
  lineItems: LineItem[];
  onClose: () => void;
}) {
  return (
    <Sheet open={!!order} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="overflow-y-auto sm:max-w-lg">
        {order && (
          <>
            <SheetHeader>
              <SheetTitle className="text-lg leading-snug">
                {order.orderNumber ?? `Order ${order.shopifyOrderId}`}
              </SheetTitle>
              <div className="flex items-center gap-2 pt-1">
                {order.financialStatus && (
                  <Badge
                    variant="outline"
                    className={`text-xs capitalize ${statusColor(order.financialStatus)}`}
                  >
                    {order.financialStatus.replace(/_/g, " ")}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground capitalize">
                  {order.fulfillmentStatus ?? "unfulfilled"}
                </span>
              </div>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              <div>
                <h4 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Order Info
                </h4>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Customer</dt>
                  <dd className="font-medium">
                    {order.customerFirstName || order.customerLastName
                      ? `${order.customerFirstName ?? ""} ${order.customerLastName ?? ""}`.trim()
                      : "\u2014"}
                  </dd>
                  <dt className="text-muted-foreground">Email</dt>
                  <dd>{order.customerEmail ?? order.email ?? "\u2014"}</dd>
                  <dt className="text-muted-foreground">Total</dt>
                  <dd className="font-medium tabular-nums">
                    {formatCurrency(order.totalPrice, order.currency)}
                  </dd>
                  <dt className="text-muted-foreground">Subtotal</dt>
                  <dd className="tabular-nums">
                    {formatCurrency(order.subtotalPrice, order.currency)}
                  </dd>
                  <dt className="text-muted-foreground">Tax</dt>
                  <dd className="tabular-nums">{formatCurrency(order.totalTax, order.currency)}</dd>
                  <dt className="text-muted-foreground">Discounts</dt>
                  <dd className="tabular-nums">
                    {formatCurrency(order.totalDiscounts, order.currency)}
                  </dd>
                  <dt className="text-muted-foreground">Currency</dt>
                  <dd>{order.currency ?? "\u2014"}</dd>
                  <dt className="text-muted-foreground">Source</dt>
                  <dd>{order.sourceName ?? "\u2014"}</dd>
                  <dt className="text-muted-foreground">Created</dt>
                  <dd>{formatDateTime(order.shopifyCreatedAt)}</dd>
                  <dt className="text-muted-foreground">Closed</dt>
                  <dd>{formatDateTime(order.closedAt)}</dd>
                  {order.cancelledAt && (
                    <>
                      <dt className="text-muted-foreground">Cancelled</dt>
                      <dd className="text-destructive">{formatDateTime(order.cancelledAt)}</dd>
                    </>
                  )}
                  <dt className="text-muted-foreground">Shopify ID</dt>
                  <dd className="font-mono text-xs">{order.shopifyOrderId}</dd>
                </dl>
              </div>

              {lineItems.length > 0 && (
                <div>
                  <h4 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Line Items ({lineItems.length})
                  </h4>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead className="text-xs">Product</TableHead>
                          <TableHead className="text-xs">SKU</TableHead>
                          <TableHead className="text-xs text-right">Qty</TableHead>
                          <TableHead className="text-xs text-right">Price</TableHead>
                          <TableHead className="text-xs text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lineItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="text-sm max-w-[180px] truncate">
                              {item.title ?? "\u2014"}
                            </TableCell>
                            <TableCell className="text-xs font-mono text-muted-foreground">
                              {item.sku ?? "\u2014"}
                            </TableCell>
                            <TableCell className="text-sm text-right tabular-nums">
                              {item.quantity ?? 0}
                            </TableCell>
                            <TableCell className="text-sm text-right tabular-nums">
                              {formatCurrency(item.price, order.currency)}
                            </TableCell>
                            <TableCell className="text-sm text-right tabular-nums font-medium">
                              {formatCurrency(
                                item.price !== null && item.quantity !== null
                                  ? item.price * item.quantity
                                  : null,
                                order.currency,
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
